import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Button, Table, Badge, Card, Spinner, Alert } from "react-bootstrap";
import { useSelector } from "react-redux";
import api from "../../utils/api";
import "./userdash.css";

const Deposit = () => {
    const { user } = useSelector((state) => state.user);
    const [amount, setAmount] = useState("");
    const [mobile, setMobile] = useState("");
    const [loading, setLoading] = useState(false);
    const [deposits, setDeposits] = useState([]);
    const [bonuses, setBonuses] = useState([]);
    const [message, setMessage] = useState(null);

    const fetchMyDeposits = async () => {
        try {
            const res = await api.get("/deposits/my");
            setDeposits(res.data.deposits);
        } catch (error) {
            console.error("Error fetching deposits", error);
        }
    };

    const fetchBonuses = async () => {
        try {
            const res = await api.get("/bonus");
            const activeDepositBonuses = (res.data.settings || []).filter(
                (b) => b.type === "deposit" && b.isActive
            );
            setBonuses(activeDepositBonuses);
        } catch (error) {
            console.error("Error fetching bonuses", error);
        }
    };

    useEffect(() => {
        fetchMyDeposits();
        fetchBonuses();
        if (user?.mobile) {
            setMobile(user.mobile);
        }
    }, [user]);

    const handleDeposit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage(null);
        try {
            const res = await api.post("/deposits", {
                amount: Number(amount),
                mobile: mobile
            });
            if (res.data.success && res.data.payment_url) {
                // Redirect to IMB Payment Gateway
                window.location.href = res.data.payment_url;
            } else {
                setMessage({ type: "success", text: "Deposit request created! Wait for approval." });
                setAmount("");
                fetchMyDeposits();
            }
        } catch (error) {
            setMessage({ type: "danger", text: error.response?.data?.message || "Deposit failed" });
        } finally {
            if (!message) setLoading(false);
        }
    };

    return (
        <Container className="my-4">
            <Row>
                <Col md={12}>
                    <Card className="bg-dark text-white border-secondary mb-4">
                        <Card.Header className="border-secondary">
                            <h4 className="mb-0">Quick Deposit</h4>
                        </Card.Header>
                        <Card.Body>
                            <Form onSubmit={handleDeposit}>
                                <Row className="align-items-end">
                                    <Col md={4}>
                                        <Form.Group className="mb-3 mb-md-0">
                                            <Form.Label>Mobile Number</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Enter mobile number"
                                                value={mobile}
                                                onChange={(e) => setMobile(e.target.value)}
                                                className="bg-secondary text-white border-0"
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3 mb-md-0">
                                            <Form.Label>Amount (₹1 - ₹1,00,000)</Form.Label>
                                            <Form.Control
                                                type="number"
                                                placeholder="Enter amount"
                                                value={amount}
                                                onChange={(e) => setAmount(e.target.value)}
                                                className="bg-secondary text-white border-0"
                                                min="1"
                                                max="100000"
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Button
                                            type="submit"
                                            variant="warning"
                                            className="w-100 fw-bold"
                                            disabled={loading}
                                        >
                                            {loading ? <Spinner size="sm" /> : "Deposit Now"}
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                            {message && <Alert variant={message.type} className="mt-3">{message.text}</Alert>}

                            {bonuses.length > 0 && (
                                <div className="mt-4 pt-3 border-top border-secondary">
                                    <h6 className="text-warning mb-3">Available Deposit Bonuses</h6>
                                    <Row>
                                        {bonuses.map((bonus) => {
                                            const isEligible = amount && Number(amount) >= bonus.minRequirementAmount;
                                            return (
                                                <Col key={bonus._id} md={6} lg={4} className="mb-2">
                                                    <div className={`p-2 rounded border ${isEligible ? 'border-success bg-opacity-10 bg-success' : 'border-secondary bg-dark'}`} style={{ fontSize: '0.85rem' }}>
                                                        <div className="d-flex justify-content-between align-items-start">
                                                            <strong className="text-white">{bonus.name}</strong>
                                                            {isEligible && <Badge bg="success">Eligible</Badge>}
                                                        </div>
                                                        <div className="text-muted small">
                                                            Min Deposit: ₹{bonus.minRequirementAmount}
                                                        </div>
                                                        <div className="text-warning fw-bold">
                                                            Reward: {bonus.rewardType === 'fixed' ? `₹${bonus.rewardAmount}` : `${bonus.rewardAmount}%`}
                                                        </div>
                                                    </div>
                                                </Col>
                                            );
                                        })}
                                    </Row>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={12}>
                    <Card className="bg-dark text-white border-secondary">
                        <Card.Header className="border-secondary">
                            <h4 className="mb-0">Recent Deposits</h4>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Table responsive variant="dark" hover className="mb-0">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Transaction ID</th>
                                        <th>Amount</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {deposits.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="text-center py-4">No deposits found</td>
                                        </tr>
                                    ) : (
                                        deposits.map((d) => (
                                            <tr key={d._id}>
                                                <td className="small">{new Date(d.createdAt).toLocaleString()}</td>
                                                <td>{d.transactionId}</td>
                                                <td className="fw-bold">₹{d.amount}</td>
                                                <td>
                                                    <Badge bg={d.status === "success" ? "success" : d.status === "pending" ? "warning" : "danger"}>
                                                        {d.status.toUpperCase()}
                                                    </Badge>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default Deposit;
